/**
 * "How this works" content for every screen in this module.
 *
 * PORTED, NOT WRITTEN. Each constant below is the help object from the source view named
 * above it, carried across as-is — same purpose text, same bullets, same definitions, same
 * emoji, same `<strong>`/`<em>` markup and HTML entities. The source authored this copy for
 * these screens and it is part of the screen (CLAUDE.md §1.2).
 *
 * The ONLY thing changed is `links[].href`: the source's own routes (`/value-roi/...`,
 * `/setup/...`) become this platform's (`/portfolio_definition/...`), because a link to a
 * path that does not exist here is worse than no link.
 *
 * Screens with no entry are the ones the source has no help object for — Program Quick
 * Start and Delivery Pathway, which are this platform's own, and the User Guide and Help &
 * Support, which ARE the help.
 */

import type { HelpContent } from './HelpPanel';

/** financial-modeling/configuration — cloned from `views/value-roi/configuration.ejs`. */
export const FIN_CONFIGURATION: HelpContent = {
    title: 'Value Model Configuration',
    icon: '⚙️',
    purpose: "This is the <strong>control panel</strong> for the MVP Value Model. The thresholds and factors you set here drive how every opportunity's ROI is <strong>classified</strong> and how its <strong>sensitivity</strong> (best/worst case) is calculated across the whole platform.",
    doHere: [
      "Set the <strong>High</strong> and <strong>Medium Value ROI thresholds</strong> and the <strong>Max Payback Years</strong> that decide each opportunity's value classification.",
      "Tune the <strong>Sensitivity Analysis</strong> best- and worst-case factors for build cost and savings.",
      "Manage the catalog of <strong>cost types</strong> reused when entering costs in Cost &amp; Scenarios.",
      "Watch the live <strong>Classification Preview</strong> update, then <strong>Save</strong> to apply across all models."
    ],
    fits: 'Financial Modeling &mdash; configure <strong>once</strong>, then every MVP model uses these settings',
    firstTime: "Confirm the defaults (High 100%, Medium 30%, Max Payback 3 yrs) make sense for your organization, adjust if needed, and click <strong>Save</strong>.",
    sections: [
      {
        label: 'Value classification tiers',
        type: 'defs',
        items: [
          { term: 'High Value', color: '#2e7d32', def: "ROI <strong>at or above</strong> the High threshold (default 100%). The strongest candidates." },
          { term: 'Medium Value', color: '#ef6c00', def: "ROI <strong>at or above</strong> Medium but below High (default 30&ndash;100%). Worth pursuing with scrutiny." },
          { term: 'Low Value', color: '#c62828', def: "ROI <strong>below</strong> the Medium threshold, or payback beyond the Max Payback Years. Likely defer or rework." }
        ],
        note: "These tiers feed the <strong>Financial Summary</strong> value badge and the MVP Value Strength score."
      },
      {
        label: 'Sensitivity factors',
        type: 'defs',
        items: [
          { term: 'Best Case', color: '#1976d2', def: "Multiplier for the optimistic scenario &mdash; e.g. build cost &times;0.80 (20% cheaper) or savings &times;1.20 (20% more)." },
          { term: 'Worst Case', color: '#c62828', def: "Multiplier for the pessimistic scenario &mdash; e.g. build cost &times;1.30 (30% pricier) or savings &times;0.80 (20% less)." }
        ],
        note: "Used to bracket ROI and payback so a model shows a <strong>range</strong>, not just a single point estimate."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Cost & Scenarios →', href: '/portfolio_definition/financial-modeling/mvp-value-model/cost-scenarios' }
    ]
  };

/** financial-modeling/scenario-setup — cloned from `views/npv-value-model/scenario-setup.ejs`. */
export const SCENARIO_SETUP: HelpContent = {
    title: 'Scenario Setup',
    icon: '🗂️',
    purpose: "Define the reusable <strong>scenario types</strong> &mdash; like Base Case, Best Case, Conservative &mdash; that every financial model selects from. Set these up <strong>once</strong> and they power costing and analysis across the MVP and NPV models.",
    doHere: [
      "Click <strong>➕ Add Scenario</strong> to create a scenario type with a name, code and description.",
      "Tag each scenario with a <strong>usage context</strong> &mdash; <em>MVP</em> or <em>NPV</em> &mdash; and filter the table by it.",
      "Mark one as the <strong>default</strong> and toggle <strong>active</strong> state to control what appears downstream.",
      "Order scenarios with <strong>sort order</strong> so they list consistently in the cost screens."
    ],
    fits: 'Financial Modeling &mdash; shared setup feeding <strong>Cost &amp; Scenarios</strong> and <strong>NPV Cost &amp; Scenarios</strong>',
    firstTime: "Confirm a sensible <strong>default</strong> scenario exists (e.g. Base Case). Add or deactivate scenario types so each model only offers the ones you want.",
    sections: [
      {
        label: 'Usage context',
        type: 'defs',
        items: [
          { term: 'MVP', color: '#4caf50', def: "Scenario is available in the <strong>MVP Value Model</strong> &mdash; Cost &amp; Scenarios and Financial Summary." },
          { term: 'NPV', color: '#ff9800', def: "Scenario is available in the <strong>NPV Value Model</strong> &mdash; NPV Cost &amp; Scenarios and Calculator." }
        ],
        note: "Use the <strong>All / MVP / NPV</strong> filter buttons above the table to focus on one context."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Configuration →', href: '/portfolio_definition/financial-modeling/configuration' }
    ]
  };

/** mvp/cost-scenarios — cloned from `views/value-roi/cost-scenarios.ejs`. */
export const MVP_COST_SCENARIOS: HelpContent = {
    title: 'MVP Cost & Scenarios',
    icon: '💰',
    purpose: "This is where an opportunity's <strong>numbers begin</strong>. You enter its <strong>one-time build costs</strong> and <strong>recurring costs</strong> for a chosen <strong>model version</strong> &mdash; the foundation every ROI, payback and TCO figure across the MVP model is calculated from.",
    doHere: [
      "Pick a <strong>project</strong> and <strong>opportunity</strong>, then a <strong>model version</strong> to cost out.",
      "Enter <strong>One-Time Build Costs</strong> (implementation labor, setup, hardware) and <strong>Save Build Costs</strong>.",
      "Add <strong>recurring costs</strong> (monthly/annual hosting, support) so TCO and payback are realistic.",
      "Review the live summary cards &mdash; <strong>Build Cost</strong>, <strong>Recurring</strong>, <strong>ROI</strong>, <strong>Payback</strong> and <strong>3-year TCO</strong> &mdash; and create alternate versions to compare."
    ],
    fits: 'Financial Modeling &rarr; <strong>Cost &amp; Scenarios</strong> &rarr; Financial Summary &rarr; Version Comparison',
    firstTime: "Select a project and opportunity, choose (or add) a model version, enter your one-time build costs and click <strong>Save Build Costs</strong>.",
    sections: [
      {
        label: 'Cost frequencies',
        type: 'defs',
        items: [
          { term: 'One-Time', color: '#1565c0', def: "Up-front <strong>build cost</strong> incurred once &mdash; implementation labor, cloud setup, training, hardware. Drives ROI and payback denominators." },
          { term: 'Monthly', color: '#7b1fa2', def: "Recurring cost billed each month; annualized as &times;12 in the Year-1 grand total." },
          { term: 'Annual', color: '#00796b', def: "Recurring cost billed once a year &mdash; hosting, licenses, support &mdash; feeding TCO and run-rate." }
        ],
        note: "<strong>Depreciable</strong> one-time items (e.g. Hardware/Tools) are spread over their configured lifespan with straight-line depreciation; other one-time costs are expensed immediately."
      },
      {
        label: 'Key derived metrics',
        type: 'defs',
        items: [
          { term: 'ROI', color: '#2e7d32', def: "Annual Savings &divide; One-Time Build Cost &times; 100. Classified High/Medium/Low using the thresholds in <strong>Configuration</strong>." },
          { term: 'Payback', color: '#ef6c00', def: "One-Time Build Cost &divide; Annual Savings, in years &mdash; how long until the build pays for itself." },
          { term: 'TCO (3yr)', color: '#5e35b1', def: "Build Cost + (Recurring &times; 3) &mdash; total cost of ownership over three years." }
        ]
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Financial Summary →', href: '/portfolio_definition/financial-modeling/mvp-value-model/financial-summary' }
    ]
  };

/** mvp/financial-summary — cloned from `views/value-roi/financial-summary.ejs`. */
export const MVP_FINANCIAL_SUMMARY: HelpContent = {
    title: 'MVP Financial Summary',
    icon: '📊',
    purpose: "The <strong>at-a-glance verdict</strong> for an opportunity's MVP business case. It reads the costs you entered in Cost &amp; Scenarios and presents the headline metrics &mdash; <strong>ROI</strong>, <strong>payback</strong>, <strong>annual savings</strong>, <strong>TCO</strong> and a <strong>value classification</strong> &mdash; per model version.",
    doHere: [
      "Select a <strong>project</strong>, <strong>opportunity</strong> and <strong>model version</strong> to summarize.",
      "Read the KPI cards: <strong>ROI</strong>, <strong>Payback (Years)</strong>, <strong>Annual Savings</strong> and the <strong>Value Classification</strong> badge.",
      "Check the <strong>MVP Value Strength</strong> score &mdash; a blended verdict computed from the configured thresholds.",
      "Switch versions to see how each cost scenario changes the case before you commit."
    ],
    fits: 'Cost &amp; Scenarios &rarr; <strong>Financial Summary</strong> &rarr; Version Comparison &rarr; Portfolio Optimizer',
    firstTime: "Pick a project and opportunity, choose a model version, and read the ROI and Value Classification at the top. No costs yet? Start in <strong>Cost &amp; Scenarios</strong>.",
    sections: [
      {
        label: 'Value classification',
        type: 'defs',
        items: [
          { term: 'High Value', color: '#2e7d32', def: "ROI at or above the High threshold &mdash; a strong, fund-it candidate." },
          { term: 'Medium Value', color: '#ef6c00', def: "ROI between Medium and High &mdash; viable but worth scrutiny." },
          { term: 'Low Value', color: '#c62828', def: "ROI below Medium or payback too long &mdash; reconsider or rework." }
        ],
        note: "Thresholds come from <strong>Value Model Configuration</strong>. The <strong>MVP Value Strength</strong> score blends ROI, payback and savings into a single label per version."
      },
      {
        label: 'Headline metrics',
        type: 'defs',
        items: [
          { term: 'ROI %', color: '#1976d2', def: "Annual savings as a percentage of the one-time build cost." },
          { term: 'Payback', color: '#ef6c00', def: "Years for cumulative savings to cover the build cost." },
          { term: 'TCO 3/5yr', color: '#5e35b1', def: "Total cost of ownership over three and five years &mdash; build plus recurring." }
        ]
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Version Comparison →', href: '/portfolio_definition/financial-modeling/mvp-value-model/version-comparison' }
    ]
  };

/** mvp/version-comparison — cloned from `views/value-roi/version-comparison.ejs`. */
export const MVP_VERSION_COMPARISON: HelpContent = {
    title: 'MVP Version Comparison',
    icon: '⚖️',
    purpose: "Put <strong>two model versions head-to-head</strong>. Pick <strong>Version A</strong> and <strong>Version B</strong> for the same opportunity and see every financial metric &mdash; build cost, run rates, savings, ROI, payback &mdash; side by side with the <strong>difference</strong> highlighted.",
    doHere: [
      "Choose a <strong>project</strong> and <strong>opportunity</strong>, then select <strong>Version A (Left)</strong> and <strong>Version B (Right)</strong>.",
      "Read the summary cards for the headline <strong>delta</strong> between the two versions.",
      "Scan the comparison grid &mdash; <strong>green</strong> means B is better, <strong>red</strong> means worse, on each metric.",
      "Decide which version to carry forward into the roadmap and portfolio."
    ],
    fits: 'Financial Summary &rarr; <strong>Version Comparison</strong> &rarr; Portfolio Optimizer &rarr; Roadmap',
    firstTime: "Select an opportunity that has at least two model versions, set A and B, and compare their Build Cost and ROI rows.",
    sections: [
      {
        label: 'Reading the difference column',
        type: 'defs',
        items: [
          { term: 'Positive', color: '#2e7d32', def: "Version B is the <strong>better</strong> outcome on this metric &mdash; e.g. lower cost or shorter payback." },
          { term: 'Negative', color: '#c62828', def: "Version B is the <strong>worse</strong> outcome &mdash; e.g. higher cost or longer payback." },
          { term: 'Neutral', color: '#607d8b', def: "No meaningful difference, or a metric where direction isn't inherently good or bad (e.g. run rate)." }
        ],
        note: "The sign convention favors the cheaper / faster / higher-return version of B over A &mdash; not just the raw arithmetic sign."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Portfolio Optimizer →', href: '/portfolio_definition/portfolio-optimizer/strategy-and-constraints' }
    ]
  };

/** npv/calculator — cloned from `views/npv-value-model/calculator.ejs`. */
export const NPV_CALCULATOR: HelpContent = {
    title: 'NPV Calculator',
    icon: '🧮',
    purpose: "The <strong>heart of the NPV model</strong>. It projects an opportunity's yearly cash flows over a time horizon, applies a <strong>discount rate</strong>, and returns its <strong>Net Present Value</strong> &mdash; the rigorous, time-value-adjusted business case.",
    doHere: [
      "Select a <strong>project</strong>, <strong>opportunity</strong> and <strong>model version</strong> to evaluate.",
      "Set the <strong>Discount Rate (%)</strong> and <strong>Time Horizon (Years)</strong> assumptions.",
      "Choose the <strong>Cash Flow Basis</strong> &mdash; Annual Savings or Annual Business Benefit &mdash; then recalculate.",
      "Read the <strong>NPV</strong>, value classification and the year-by-year cash-flow table, then <strong>Update NPV on Opportunity</strong> to save it."
    ],
    fits: 'NPV Cost &amp; Scenarios &rarr; <strong>NPV Calculator</strong> &rarr; Scenario Comparison &rarr; Portfolio Optimizer',
    firstTime: "Pick an opportunity with costs entered, set a discount rate (default 10%) and a 5-year horizon, and read the resulting NPV. Use <strong>Manage Costs</strong> if no costs exist yet.",
    sections: [
      {
        label: 'The key assumptions',
        type: 'defs',
        items: [
          { term: 'Discount Rate', color: '#1976d2', def: "Your cost of capital &mdash; the annual rate future cash flows are discounted by. Higher rate, lower NPV." },
          { term: 'Horizon', color: '#5e35b1', def: "Number of years the model projects cash flows over before stopping." },
          { term: 'Cash Flow Basis', color: '#00796b', def: "Whether each year's inflow is the opportunity's <strong>Annual Savings</strong> or its broader <strong>Annual Business Benefit</strong>." }
        ],
        note: "A <strong>positive NPV</strong> means the discounted benefits outweigh the costs over the horizon &mdash; the investment creates value."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: NPV Scenario Comparison →', href: '/portfolio_definition/financial-modeling/npv-value-model/scenario-comparison' }
    ]
  };

/** npv/comparison — cloned from `views/npv-value-model/comparison.ejs`. */
export const NPV_COMPARISON: HelpContent = {
    title: 'NPV Scenario Comparison',
    icon: '🔬',
    purpose: "Run the NPV calculation across <strong>all of an opportunity's model versions at once</strong> under the same discount rate and horizon, then rank them. The fastest way to see which scenario delivers the strongest <strong>discounted</strong> return.",
    doHere: [
      "Select a <strong>project</strong> and <strong>opportunity</strong> to compare every model version of.",
      "Set a shared <strong>Discount Rate</strong> and <strong>Horizon</strong> so all versions are judged on equal terms.",
      "Read the table &mdash; <strong>Build Cost</strong>, <strong>Annual Operating</strong>, <strong>Annual Benefit</strong>, <strong>NPV</strong>, <strong>ROI %</strong>, <strong>Payback</strong> and <strong>Value</strong> &mdash; one row per version.",
      "Pick the winning version to carry into optimization and the roadmap."
    ],
    fits: 'NPV Calculator &rarr; <strong>NPV Scenario Comparison</strong> &rarr; Portfolio Optimizer &rarr; Roadmap',
    firstTime: "Choose an opportunity with two or more model versions, set the discount rate and horizon, and compare their NPV column to find the strongest scenario.",
    sections: [
      {
        label: 'Value badge',
        type: 'defs',
        items: [
          { term: 'High', color: '#2e7d32', def: "Strong discounted return &mdash; comfortably positive NPV and high ROI." },
          { term: 'Medium', color: '#ef6c00', def: "Acceptable return &mdash; positive but more modest NPV." },
          { term: 'Low', color: '#c62828', def: "Weak or negative NPV &mdash; the discounted costs rival or exceed the benefits." }
        ],
        note: "Compare versions on <strong>NPV</strong> first (absolute value created) and <strong>payback</strong> second (how fast it returns). A single shared discount rate keeps the comparison fair."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Portfolio Optimizer →', href: '/portfolio_definition/portfolio-optimizer/strategy-and-constraints' }
    ]
  };

/** npv/npv-costs — cloned from `views/npv-value-model/npv-costs.ejs`. */
export const NPV_COST_SCENARIOS: HelpContent = {
    title: 'NPV Cost & Scenarios',
    icon: '🧾',
    purpose: "Enter the <strong>costs</strong> that feed the NPV Value Model. Like its MVP counterpart you capture <strong>one-time build</strong> and <strong>recurring</strong> costs per <strong>model version</strong> &mdash; but here they become the yearly cash outflows the <strong>NPV Calculator</strong> discounts over its horizon.",
    doHere: [
      "Pick a <strong>project</strong>, <strong>opportunity</strong>, <strong>scenario</strong> and <strong>model version</strong> to cost.",
      "Enter <strong>one-time build costs</strong> and <strong>recurring</strong> (monthly/annual) costs.",
      "Review the totals &mdash; one-time, monthly, annual and grand total &mdash; that flow into the cash-flow projection.",
      "Build alternate scenarios so the <strong>NPV Calculator</strong> and <strong>Scenario Comparison</strong> have versions to discount and rank."
    ],
    fits: 'Financial Modeling &rarr; <strong>NPV Cost &amp; Scenarios</strong> &rarr; NPV Calculator &rarr; Scenario Comparison',
    firstTime: "Select a project and opportunity, choose a scenario and model version, and enter the one-time build costs. Then open the <strong>NPV Calculator</strong> to discount them.",
    sections: [
      {
        label: 'Cost frequencies',
        type: 'defs',
        items: [
          { term: 'One-Time', color: '#6a1b9a', def: "Up-front build cost incurred once &mdash; treated as the initial (year 0) cash outflow in the NPV model." },
          { term: 'Monthly', color: '#7b1fa2', def: "Recurring monthly cost, annualized into each year's operating outflow." },
          { term: 'Annual', color: '#00796b', def: "Recurring yearly cost &mdash; hosting, licenses, support &mdash; applied every horizon year." }
        ],
        note: "<strong>Depreciable</strong> one-time items are spread over their lifespan; other one-time costs are expensed immediately. The NPV Calculator then discounts these flows by your chosen rate."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: NPV Calculator →', href: '/portfolio_definition/financial-modeling/npv-value-model/npv-calculator' }
    ]
  };

/** opportunities/completed — cloned from `views/opportunities-scoring-completed.ejs`. */
export const SCORING_COMPLETED: HelpContent = {
    title: 'Completed Scoring',
    icon: '✅',
    purpose: "The <strong>results board</strong>. Opportunities here have every framework question answered and carry an <strong>overall score</strong> and a <strong>recommendation</strong> &mdash; this is where scoring outcomes feed the rest of the journey.",
    doHere: [
      "Review each opportunity's <strong>overall score</strong>, <strong>recommendation tier</strong> and <strong>scored date</strong>.",
      "Use <strong>👁️ View Details</strong> to see the full breakdown, or <strong>🔄 Re-Score</strong> to revisit answers &mdash; <strong>blocked 🔒</strong> once the opportunity is <em>Approved</em> or once <em>effort planning</em> exists for it (see the rules below).",
      "Click <strong>Download to Excel</strong> to export the completed set with a filename of your choice.",
      "Confirm the scores you'll carry forward into <strong>Financial Modeling</strong> and the <strong>Roadmap</strong>."
    ],
    fits: 'In Progress &rarr; <strong>Completed</strong> &rarr; Financials &rarr; Optimize &rarr; Roadmap',
    firstTime: "Open an opportunity with <strong>👁️ View Details</strong> to see how its score broke down across dimensions, then move on to financial modeling.",
    sections: [
      {
        label: 'When can you Re-Score? (🔄 vs 🔒)',
        type: 'defs',
        items: [
          { term: '🔄 Re-Score allowed', color: '#2e7d32', def: "The opportunity is fully <strong>Scored</strong>, <strong>not yet Approved</strong>, and has <strong>no effort/resource planning</strong> recorded. Re-Score reopens its answers for editing." },
          { term: '🔒 Blocked — Approved', color: '#c62828', def: "Once an opportunity is <strong>Approved</strong>, its scores are locked. Re-Score is disabled to protect the approved decision." },
          { term: '🔒 Blocked — effort exists', color: '#c62828', def: "If <strong>effort planning records exist</strong> for the opportunity, Re-Score is disabled &mdash; re-scoring could invalidate effort/resource estimates already built on the score. Remove the effort plan first if a re-score is truly needed." },
          { term: 'Not fully scored?', color: '#1976d2', def: "Opportunities that are <strong>not completely scored</strong> don't appear here &mdash; you <strong>continue scoring</strong> them from <strong>Scoring → In Progress</strong> (or the <strong>Queue</strong>). Continuation is always possible until scoring is complete." }
        ]
      },
      {
        label: 'How an opportunity reaches scoring (Submit to Score)',
        type: 'defs',
        items: [
          { term: 'Approved only', color: '#00897b', def: "<strong>Submit to Score</strong> is only available once an opportunity is <strong>Approved</strong>. On non-approved opportunities the button is <strong>visible but disabled</strong> &mdash; approve it first." },
          { term: 'Framework ready', color: '#5e35b1', def: "The opportunity's <strong>framework</strong> must be <em>Design Complete</em> or <em>Locked</em> before it can be submitted to scoring." }
        ]
      },
      {
        label: 'Recommendation tiers (overall score)',
        type: 'defs',
        items: [
          { term: 'Fast Track', color: '#2e7d32', def: "<strong>4.25 &ndash; 5.00</strong> &mdash; a standout case; prioritize it for delivery." },
          { term: 'Recommended', color: '#43a047', def: "<strong>3.50 &ndash; 4.24</strong> &mdash; a solid candidate worth proceeding with." },
          { term: 'Conditional', color: '#fb8c00', def: "<strong>2.50 &ndash; 3.49</strong> &mdash; proceed only if specific concerns are addressed." },
          { term: 'Major Concerns', color: '#e53935', def: "<strong>2.00 &ndash; 2.49</strong> &mdash; significant risk; needs rework before it advances." },
          { term: 'Discontinue', color: '#b71c1c', def: "<strong>1.00 &ndash; 1.99</strong> &mdash; discontinue or defer; the case doesn't hold up." }
        ],
        note: "The recommendation is derived from the weighted overall score across the framework's dimensions and components."
      },
      {
        label: 'Statuses you may see',
        type: 'defs',
        items: [
          { term: 'Scored', color: '#4caf50', def: "Scoring is complete; the opportunity is awaiting an approval decision." },
          { term: 'Approved', color: '#00897b', def: "Green-lit for delivery &mdash; rescoring is locked to preserve the approved business case." }
        ],
        note: "Approved and beyond (<em>In Development</em>, <em>Delivered</em>) opportunities remain visible here as a scoring record."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Financial Modeling →', href: '/portfolio_definition/financial-modeling/configuration' }
    ]
  };

/** opportunities/dashboard — cloned from `views/opportunities-dashboard.ejs`. */
export const OPPORTUNITIES_DASHBOARD: HelpContent = {
    title: 'Opportunity Dashboard',
    icon: '📊',
    purpose: "A read-only <strong>analytics overview</strong> of your whole opportunity portfolio &mdash; how many you have, how many are scored, how they distribute across statuses and process groups, and which scored highest. Use it to see the shape of the pipeline at a glance.",
    doHere: [
      "Read the <strong>KPI cards</strong> &mdash; total, scored, pending and average score across all opportunities.",
      "See the <strong>status distribution</strong> to know how many sit at each stage of the lifecycle.",
      "Review the <strong>process-group (L1) distribution</strong> and the <strong>top-scored</strong> opportunities.",
      "Click any KPI card to jump straight into the <strong>Registry</strong> filtered to that set."
    ],
    fits: 'Discover &rarr; <strong>Score</strong> &rarr; Finance &rarr; Optimize &rarr; Plan &rarr; Execute',
    firstTime: "Scan the <strong>KPI cards</strong> at the top, then click <strong>Total Opportunities</strong> to open the full Registry.",
    sections: [
      {
        label: 'What the cards mean',
        type: 'defs',
        items: [
          { term: 'Total', color: '#1976d2', def: "Every opportunity in the registry across all projects, regardless of status." },
          { term: 'Scored', color: '#388e3c', def: "Opportunities that have completed scoring and carry a recommendation." },
          { term: 'Pending', color: '#f57c00', def: "Opportunities not yet scored — still in Draft, Ready or in-progress states." },
          { term: 'Avg Score', color: '#5e35b1', def: "The mean overall score across all scored opportunities — a portfolio health signal." }
        ],
        note: "This screen is a window into data created elsewhere — add and score opportunities in the <strong>Registry</strong> and <strong>Scoring</strong> screens."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Opportunity Registry →', href: '/portfolio_definition/opportunities/opportunity-registry' }
    ]
  };

/** opportunities/in-progress — cloned from `views/opportunities-scoring-in-progress.ejs`. */
export const SCORING_IN_PROGRESS: HelpContent = {
    title: 'Scoring In Progress',
    icon: '✏️',
    purpose: "Your <strong>partially-scored work</strong>. Every opportunity at status <strong>Scoring in Progress</strong> with at least one answer saved lives here, with a <em>progress bar</em> showing how many framework questions remain.",
    doHere: [
      "Track each opportunity's <strong>progress</strong> &mdash; answered questions vs. total for its framework.",
      "Click <strong>🔄 Continue Scoring</strong> to reopen the form and finish the assessment.",
      "Use <strong>👁️ View Details</strong> to inspect an opportunity without scoring.",
      "<strong>Filter</strong> and <strong>Expand / Collapse</strong> to manage a long in-flight list."
    ],
    fits: 'Scoring Queue &rarr; <strong>In Progress</strong> &rarr; Completed &rarr; Financials &rarr; Roadmap',
    firstTime: "Pick an opportunity and click <strong>🔄 Continue Scoring</strong> to answer the remaining questions and finalize its score.",
    sections: [
      {
        label: 'Reading the progress bar',
        type: 'defs',
        items: [
          { term: 'Partial', color: '#ff9800', def: "Some questions answered, some still open &mdash; the bar shows the share complete out of the framework's total questions." },
          { term: 'Near complete', color: '#1976d2', def: "Most questions answered &mdash; finish the last few, then submit to finalize the overall score and recommendation." }
        ],
        note: "An opportunity stays here until <em>all</em> framework questions are answered and you <strong>Submit Scores</strong>, which moves it to <strong>Completed</strong>."
      },
      {
        label: 'Where it goes next',
        type: 'html',
        html: "<div style='font-size:12.5px; color:#546e7a; line-height:1.95;'><strong style='color:#ff9800;'>Scoring in Progress</strong> &rarr; (all questions answered + Submit) &rarr; <strong style='color:#4caf50;'>Scored</strong> &rarr; Approved</div>"
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Completed →', href: '/portfolio_definition/opportunities/completed' }
    ]
  };

/** opportunities/scoring — cloned from `views/opportunities-scoring.ejs`. */
export const SCORING_OVERVIEW: HelpContent = {
    title: 'Scoring Overview',
    icon: '🎯',
    purpose: "The <strong>command center</strong> for opportunity scoring. Every opportunity that's been sent to scoring flows through three stages here &mdash; <em>Queue</em>, <em>In Progress</em>, and <em>Completed</em> &mdash; each measured against its project's <strong>scoring framework</strong>.",
    doHere: [
      "See live counts of opportunities <strong>In Queue</strong>, <strong>In Progress</strong> and <strong>Completed</strong> at a glance.",
      "Jump into the <strong>Scoring Queue</strong> to start scoring anything that's ready.",
      "Pick up partially-scored work from <strong>In Progress</strong>, or review finished scores in <strong>Completed</strong>.",
      "Open the <strong>Heat Map</strong> for a visual, color-coded comparison of scored opportunities."
    ],
    fits: 'Project &rarr; Opportunities &rarr; <strong>Scoring</strong> &rarr; Financials &rarr; Roadmap &rarr; Execution',
    firstTime: "Click the <strong>In Queue</strong> card (or <strong>View Scoring Queue</strong>) to see what's ready, then open an opportunity and start scoring it.",
    sections: [
      {
        label: 'The three scoring stages',
        type: 'defs',
        items: [
          { term: 'In Queue', color: '#2196f3', def: "Opportunities at status <strong>Ready for Scoring</strong> (or in progress with no answers yet) &mdash; cleared to be scored but not yet started." },
          { term: 'In Progress', color: '#ff9800', def: "Status <strong>Scoring in Progress</strong> with at least one answer saved &mdash; partially scored work you can return to and finish." },
          { term: 'Completed', color: '#4caf50', def: "Status <strong>Scored</strong> or <strong>Approved</strong> with every framework question answered &mdash; the opportunity has an overall score and a recommendation." }
        ],
        note: "Each card is a shortcut: click it to drill into that stage's list. The <strong>Heat Map</strong> visualizes completed scores side by side."
      },
      {
        label: 'How an opportunity moves through',
        type: 'html',
        html: "<div style='font-size:12.5px; color:#546e7a; line-height:1.95;'>Ready for Scoring &rarr; <strong style='color:#ff9800;'>Scoring in Progress</strong> &rarr; <strong style='color:#4caf50;'>Scored</strong> &rarr; Approved</div>"
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Open the Scoring Queue →', href: '/portfolio_definition/opportunities/scoring-queue' }
    ]
  };

/** opportunities/scoring-queue — cloned from `views/opportunities-scoring-queue.ejs`. */
export const SCORING_QUEUE: HelpContent = {
    title: 'Scoring Queue',
    icon: '📥',
    purpose: "The <strong>to-do list</strong> for scoring. Every opportunity that's <strong>Ready for Scoring</strong> (and not yet started) appears here, grouped by project, waiting for someone to assess it against its framework.",
    doHere: [
      "Browse opportunities <strong>grouped by project</strong> &mdash; use <strong>Expand All</strong> / <strong>Collapse All</strong> to navigate large lists.",
      "<strong>Filter</strong> by project or opportunity name to find a specific item fast.",
      "Click the <strong>▶️ Start Scoring</strong> action to open the scoring form for an opportunity.",
      "Work through the queue until each item moves into <strong>In Progress</strong> or <strong>Completed</strong>."
    ],
    fits: 'Opportunities &rarr; <strong>Scoring Queue</strong> &rarr; In Progress &rarr; Completed &rarr; Financials',
    firstTime: "Find an opportunity, click its <strong>▶️ Start Scoring</strong> button, and answer the framework questions on the scoring form.",
    sections: [
      {
        label: 'What lands in the queue',
        type: 'defs',
        items: [
          { term: 'Ready for Scoring', color: '#2196f3', def: "The opportunity has been submitted to scoring and is cleared to be assessed &mdash; this is the normal entry state." },
          { term: 'In Progress (0 answers)', color: '#90a4ae', def: "Marked <strong>Scoring in Progress</strong> but no answers saved yet, so it still sits in the queue until you record a first response." }
        ],
        note: "Once you save at least one answer, the opportunity drops off this list and shows up under <strong>In Progress</strong>."
      },
      {
        label: 'Reading the table',
        type: 'html',
        html: "<div style='font-size:12.5px; color:#546e7a; line-height:1.7;'>Columns show <strong>Project / Opportunity</strong>, <strong>Description</strong>, <strong>Owner</strong>, <strong>Submitted</strong> date, and <strong>Actions</strong>. Opportunities are nested under their project header so you can score a whole project's batch together.</div>"
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: In Progress →', href: '/portfolio_definition/opportunities/in-progress' }
    ]
  };

/** optimizer/analytics-visuals — cloned from `views/optimizer/analytics-visuals.ejs`. */
export const OPT_ANALYTICS: HelpContent = {
    title: 'Analytics & Visuals',
    icon: '📈',
    purpose: "A <strong>portfolio-wide dashboard</strong> over all your saved optimization scenarios. Instead of inspecting one run, this screen aggregates them &mdash; revealing patterns in <strong>ROI</strong>, <strong>budget utilization</strong> and <strong>weighting strategy</strong> so you can see how your optimization choices trend across the whole portfolio.",
    doHere: [
      "Scan the top <strong>stat cards</strong>: total scenarios, average ROI, total budget allocated and average opportunities per scenario.",
      "Read the charts &mdash; <strong>ROI Distribution</strong>, <strong>Budget Utilization</strong>, <strong>Weight Strategy Trends</strong>, <strong>Opportunities Selected</strong> and the <strong>Scenario Timeline</strong>.",
      "Filter by <strong>project</strong> and <strong>time period</strong> (last 30/60/90 days or all time) to narrow the view.",
      "Use <strong>🔄 Refresh</strong> to pull in the latest saved scenarios."
    ],
    fits: 'Score &rarr; Finance &rarr; <strong>Optimize</strong> &rarr; Plan &rarr; Execute',
    firstTime: "Leave the filters on <em>All Projects / All Time</em> and review the four stat cards first &mdash; they give the quickest read on how your scenarios are performing overall.",
    sections: [
      {
        label: 'What the charts tell you',
        type: 'defs',
        items: [
          { term: 'ROI Dist.', color: '#2e7d32', def: "<strong>ROI Distribution</strong> &mdash; how portfolio ROI spreads across scenarios; spot whether most strategies land high or low." },
          { term: 'Budget', color: '#1976d2', def: "<strong>Budget Utilization</strong> &mdash; budget constraint vs. actual cost selected, showing how fully each scenario spends." },
          { term: 'Weights', color: '#8e24aa', def: "<strong>Weight Strategy Trends</strong> &mdash; average weight distribution, revealing which criteria you typically prioritize." },
          { term: 'Timeline', color: '#f57c00', def: "<strong>Scenario Timeline</strong> &mdash; scenarios created over time with their key metrics, showing how strategy evolved." }
        ],
        note: "All metrics respect the active <strong>project</strong> and <strong>time period</strong> filters &mdash; change either to recompute the charts and stats."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Back to Saved Scenarios →', href: '/portfolio_definition/portfolio-optimizer/saved-scenarios' }
    ]
  };

/** optimizer/saved-scenarios — cloned from `views/optimizer/saved-scenarios.ejs`. */
export const OPT_SAVED_SCENARIOS: HelpContent = {
    title: 'Saved Scenarios',
    icon: '📋',
    purpose: "Your <strong>library of optimization runs</strong>. Every time you save a scenario in <em>Strategy and Constraints</em>, it lands here as a snapshot of one budget + weighting strategy, with its resulting cost, benefit and ROI. Use this screen to revisit, export and <strong>compare</strong> scenarios before committing to a portfolio.",
    doHere: [
      "<strong>Browse and filter</strong> saved scenarios by project or search by name.",
      "<strong>View</strong> (👁️) a scenario's details or <strong>Export to Excel</strong> (📊) its selected opportunities.",
      "Tick two or more checkboxes and click <strong>📊 Compare Scenarios</strong> to see them side by side.",
      "Use <strong>🧹 Cleanup Old Scenarios</strong> to remove runs older than 90 days and keep the list tidy."
    ],
    fits: 'Score &rarr; Finance &rarr; <strong>Optimize</strong> &rarr; Plan &rarr; Execute',
    firstTime: "If the list is empty, build a run in <strong>Strategy and Constraints</strong> first. Otherwise select two scenarios and hit <strong>Compare Scenarios</strong> to weigh them against each other.",
    sections: [
      {
        label: 'Scenario age badges',
        type: 'defs',
        items: [
          { term: 'Active', color: '#2e7d32', def: "Created within the last <strong>60 days</strong> &mdash; current and safe to rely on." },
          { term: 'Aging', color: '#e65100', def: "Between <strong>60 and 90 days</strong> old &mdash; review whether its budget and weights still reflect reality." },
          { term: 'Old', color: '#c62828', def: "Older than <strong>90 days</strong> &mdash; flagged for cleanup; the <em>Cleanup Old Scenarios</em> action removes these." }
        ],
        note: "Each row also shows <strong># Opps</strong>, <strong>Total Cost</strong>, <strong>Total Benefit</strong> and <strong>ROI %</strong> so you can scan strategies at a glance. The <strong>Compare</strong> button appears once two or more rows are selected."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Back to Strategy and Constraints →', href: '/portfolio_definition/portfolio-optimizer/strategy-and-constraints' }
    ]
  };

/** optimizer/saved-scenarios/compare — cloned from `views/optimizer/compare-scenarios.ejs`. */
export const OPT_COMPARE_SCENARIOS: HelpContent = {
    title: 'Compare Scenarios',
    icon: '\u2696\ufe0f',
    purpose: "A <strong>side-by-side comparison</strong> of two or more saved optimization scenarios. Each column is one scenario; each row is a metric &mdash; budget, total cost, total benefit, ROI, payback, opportunity count and the weight strategy &mdash; so you can see exactly how different budgets and weightings change the recommended portfolio.",
    doHere: [
      "Read each scenario <strong>column</strong> top-to-bottom and contrast the key metrics across them.",
      "Spot the trade-offs &mdash; e.g. a higher-budget scenario with stronger <strong>ROI</strong> vs. a leaner one with faster <strong>payback</strong>.",
      "Check the <strong>overlap</strong> grid to see which opportunities every scenario funds and which are unique to one.",
      "Decide which strategy to carry forward into <strong>Roadmap Planning</strong>.",
      "Return to <strong>Saved Scenarios</strong> to change the selection you're comparing."
    ],
    fits: 'Score &rarr; Finance &rarr; <strong>Optimize</strong> &rarr; Plan &rarr; Execute',
    firstTime: "This screen loads the scenarios you ticked on <strong>Saved Scenarios</strong>. To compare a different set, go back there, tick the scenarios you want, and click <em>Compare Scenarios</em>.",
    sections: [
      {
        label: 'Metrics compared',
        type: 'defs',
        items: [
          { term: 'ROI %', color: '#2e7d32', def: "<strong>Portfolio return on investment</strong> &mdash; total benefit relative to total cost for the selected mix." },
          { term: 'Payback', color: '#1976d2', def: "Months to recover the portfolio's cost from its annual benefit &mdash; lower is faster." },
          { term: 'Cost / Benefit', color: '#f57c00', def: "Total <strong>cost</strong> of selected opportunities vs. their total <strong>annual benefit</strong>." },
          { term: '# Opps', color: '#5e35b1', def: "How many opportunities the scenario funds within its budget." }
        ],
        note: "There is no single 'best' &mdash; the right scenario depends on whether you optimize for return, speed of payback, or breadth of coverage."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Back to Saved Scenarios \u2192', href: '/portfolio_definition/portfolio-optimizer/saved-scenarios' }
    ]
  };

/** optimizer/strategy-and-constraints — cloned from `views/optimizer/scenario-builder.ejs`. */
export const OPT_SCENARIO_BUILDER: HelpContent = {
    title: 'Strategy and Constraints',
    icon: '🎛️',
    purpose: "This is the <strong>what-if engine</strong> of the Portfolio Optimizer. Set a <strong>budget</strong> and six <strong>strategic weights</strong>, then run the optimizer &mdash; it ranks every opportunity by a weighted <strong>portfolio score</strong> and greedily picks the highest-value mix that <em>fits within budget</em>. Save the result as a reusable <strong>scenario</strong>.",
    doHere: [
      "<strong>Pane A &mdash; Constraints &amp; Strategy:</strong> pick a project, optionally override its budget, and tune the six weight sliders (must total <strong>100%</strong>).",
      "<strong>Pane B &mdash; Ranked Opportunities:</strong> after running, review the ranked table; <strong>Lock</strong> must-haves in, <strong>Exclude</strong> items out, then re-run.",
      "<strong>Pane C &mdash; Portfolio Summary:</strong> read the KPIs (cost, benefit, ROI, payback, budget remaining) and feasibility checks.",
      "Click <strong>🚀 Run Optimization</strong>, then <strong>Save Scenario</strong> to keep the result."
    ],
    fits: 'Score &rarr; Finance &rarr; <strong>Optimize</strong> &rarr; Plan &rarr; Execute',
    firstTime: "Choose a project in Pane A, leave the default weights, and click <strong>🚀 Run Optimization</strong>. Then lock or exclude a few items in Pane B and re-run to see the selection change.",
    sections: [
      {
        label: 'The five portfolio weights',
        type: 'defs',
        items: [
          { term: 'W1 ROI', color: '#1976d2', def: "<strong>Return on Investment</strong> &mdash; rewards opportunities with strong financial returns relative to cost." },
          { term: 'W2 Critical', color: '#00897b', def: "<strong>Business Criticality</strong> &mdash; favors items the business most depends on." },
          { term: 'W3 Align', color: '#5e35b1', def: "<strong>Strategic Alignment</strong> &mdash; weights fit with stated strategic goals." },
          { term: 'W4 B/C', color: '#f57c00', def: "<strong>Benefit/Cost Effectiveness</strong> &mdash; value delivered per dollar spent." },
          { term: 'W5 Risk', color: '#d32f2f', def: "<strong>Delivery Risk</strong> &mdash; <em>subtracted</em> from the score, so riskier items rank lower." }
        ],
        note: "The portfolio score is <strong>w&#8321;&middot;ROI + w&#8322;&middot;Criticality + w&#8323;&middot;Alignment + w&#8324;&middot;(B/C) &minus; w&#8325;&middot;Risk</strong>. Sliders must sum to <strong>100%</strong> &mdash; the total turns red until they do, and every one of the five changes the ranking."
      },
      {
        label: 'Lock, Exclude & Mandatory',
        type: 'defs',
        items: [
          { term: 'Lock', color: '#9e9e9e', def: "<strong>Force in</strong> &mdash; the optimizer must keep this opportunity in the selection regardless of score or budget pressure." },
          { term: 'Exclude', color: '#757575', def: "<strong>Force out</strong> &mdash; the opportunity is removed from consideration for this run." },
          { term: 'Mandatory', color: '#4caf50', def: "Items flagged mandatory are always kept when <em>Respect Mandatory</em> is on (it is by default)." }
        ],
        note: "Constraint checkboxes (<em>Include only feasible</em>, <em>Exclude high-risk</em>, <em>Respect mandatory</em>) filter the candidate set before ranking. Changing any input marks results <strong>dirty</strong> &mdash; re-run before saving."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Saved Scenarios →', href: '/portfolio_definition/portfolio-optimizer/saved-scenarios' }
    ]
  };

/** projects/dashboard — cloned from `views/projects-dashboard.ejs`. */
export const PROJECTS_DASHBOARD: HelpContent = {
    title: 'Projects Dashboard',
    icon: '📊',
    purpose: "A <strong>portfolio-level read-out</strong> of every project and the opportunities inside them. Use it to see how many initiatives you have, how many opportunities are linked vs. unlinked, how they're scoring, and how budget and benefit are distributed across projects.",
    doHere: [
      "Scan the four <strong>summary cards</strong> &mdash; Total Projects, Linked Opportunities, Unlinked Opportunities and Avg Opps/Project.",
      "Use the <strong>View by</strong> selector on the chart to compare projects by opportunities, budget, annual benefit, delivery cost or average score.",
      "Read the <strong>Projects Overview</strong> table for per-project totals, scored counts and average score.",
      "Check <strong>Recent Projects</strong> for the latest additions, then click <strong>View All Projects</strong> to drill in."
    ],
    fits: 'Project &rarr; Opportunities &rarr; Scoring &rarr; Financials &rarr; Roadmap &rarr; Execution',
    firstTime: "Set the chart's <strong>View by</strong> to <em>Average Score</em> to spot which projects are scoring well, then open a project from <strong>Projects Overview</strong> to see its opportunities.",
    sections: [
      {
        label: 'The summary cards',
        type: 'defs',
        items: [
          { term: 'Total Projects', color: '#1976d2', def: "How many project workspaces exist in the portfolio." },
          { term: 'Linked Opps', color: '#4caf50', def: "Opportunities that have been assigned to a project &mdash; counted in roll-ups." },
          { term: 'Unlinked Opps', color: '#ff9800', def: "Opportunities not yet attached to any project. Link them so they're scored and tracked under an initiative." },
          { term: 'Avg Opps/Project', color: '#9c27b0', def: "Linked opportunities divided by total projects &mdash; how concentrated your portfolio is." }
        ],
        note: "<strong>Avg Score</strong> in the table is the mean weighted score of a project's scored opportunities &mdash; blank until at least one is scored."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Project Registry →', href: '/portfolio_definition/projects/project-registry' }
    ]
  };

/** projects/project-administration — cloned from `views/project-administration.ejs`. */
export const PROJECT_ADMINISTRATION: HelpContent = {
    title: 'Project Administration',
    icon: '🔐',
    purpose: "Control <strong>who can see and edit each project</strong>. This Administrator-only screen lists every project-access grant and lets you add or revoke access per user &mdash; the gatekeeper for project-level permissions across the portfolio.",
    doHere: [
      "Review the <strong>access table</strong> &mdash; which user holds what access on which project, who granted it and when.",
      "Click <strong>&#10133; Grant Access</strong> to give a user <em>Owner</em>, <em>Full</em> or <em>Read-Only</em> rights on a project.",
      "Use the <strong>Filter by Project</strong> and <strong>Filter by User</strong> dropdowns to narrow the list; <strong>Clear</strong> resets them.",
      "Click the <strong>🗑️ revoke</strong> icon to remove a grant &mdash; note <strong>Owner</strong> access cannot be revoked here."
    ],
    fits: 'Project &rarr; Opportunities &rarr; Scoring &rarr; Financials &rarr; Roadmap &rarr; Execution',
    firstTime: "Click <strong>&#10133; Grant Access</strong>, choose a project and a user, pick an <strong>Access Type</strong>, and save &mdash; the grant appears immediately in the table.",
    sections: [
      {
        label: 'Access types',
        type: 'defs',
        items: [
          { term: 'Owner', color: '#d32f2f', def: "Full control and ownership. Assigned at creation; <strong>cannot be revoked</strong> from this screen. Can manage access." },
          { term: 'Project Manager', color: '#6a1b9a', def: "<strong>Manage access + edit</strong> &mdash; can grant/revoke access to this project and edit it and its opportunities." },
          { term: 'Full', color: '#1976d2', def: "<strong>Edit</strong> &mdash; can view and edit the project and its opportunities (no access management)." },
          { term: 'Project Team Member', color: '#00838f', def: "<strong>Edit</strong> &mdash; can view and edit the project and its opportunities (no access management)." },
          { term: 'Key Stakeholder', color: '#2e7d32', def: "<strong>View only</strong> &mdash; can see the project, its opportunities and status reports, but cannot change anything." },
          { term: 'SteerCo', color: '#455a64', def: "<strong>View only</strong> &mdash; steering-committee visibility into the project and its status; cannot change anything." },
          { term: 'Read-Only', color: '#f57c00', def: "<strong>View only</strong> &mdash; can see the project and its data but cannot change anything." }
        ],
        note: "Grants are recorded with <strong>Granted By</strong> and <strong>Granted Date</strong> for an audit trail."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Back to Project Registry →', href: '/portfolio_definition/projects/project-registry' }
    ]
  };

/** settings/benefit-cost — cloned from `views/setup-benefit-cost-effectiveness.ejs`. */
export const BENEFIT_COST: HelpContent = {
    title: 'Benefit / Cost Scale',
    icon: '💰',
    purpose: "This screen defines the <strong>1&ndash;5 scoring scale</strong> for <strong>Benefit / Cost Effectiveness</strong> &mdash; the return an opportunity delivers relative to its cost. Each level pairs a definition with a <strong>payback period</strong> and <strong>benefit multiple</strong> so scorers rate ROI consistently.",
    doHere: [
      "Review the <strong>five levels</strong> and the ROI band each represents.",
      "Click <strong>Edit</strong> to set a level's <em>name</em>, <em>description</em>, <em>payback period</em> and <em>benefit multiple</em>.",
      "Use payback / multiple thresholds (e.g. <em>&lt; 6 months</em>, <em>&gt; 5x</em>) to make rating objective.",
      "Toggle a level <strong>active</strong> to control its availability as a scoring option."
    ],
    fits: 'Frameworks / Scoring config &rarr; <strong>Scoring criteria</strong> &rarr; Opportunity scoring &rarr; Recommendation',
    firstTime: "Open <strong>Edit</strong> on the top level and confirm its <em>payback period</em> and <em>benefit multiple</em> match your investment hurdle.",
    sections: [
      {
        label: 'The 1–5 benefit / cost scale',
        type: 'defs',
        items: [
          { term: 'Score 5', color: '#4CAF50', def: "<strong>Exceptional ROI</strong> &mdash; fast payback and a high benefit multiple." },
          { term: 'Score 4', color: '#8BC34A', def: "<strong>Strong ROI</strong> &mdash; clear, well above the investment hurdle." },
          { term: 'Score 3', color: '#FFC107', def: "<strong>Adequate ROI</strong> &mdash; positive return, moderate payback." },
          { term: 'Score 2', color: '#FF9800', def: "<strong>Marginal ROI</strong> &mdash; slow payback or thin benefit multiple." },
          { term: 'Score 1', color: '#F44336', def: "<strong>Poor ROI</strong> &mdash; cost outweighs benefit or payback is too long." }
        ],
        note: "Each level can carry a <strong>payback period</strong> and <strong>benefit multiple</strong> band to make scoring objective rather than subjective."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Opportunity scoring →', href: '/portfolio_definition/opportunities/opportunity-registry' }
    ]
  };

/** settings/business-value-criteria — cloned from `views/setup-business-criticality.ejs`. */
export const BUSINESS_VALUE_CRITERIA: HelpContent = {
    title: 'Business Criticality Scale',
    icon: '🏢',
    purpose: "This screen defines the <strong>1&ndash;5 scoring scale</strong> for <strong>Business Criticality</strong> &mdash; how important an initiative is to the business. These level definitions and their typical characteristics are the rubric scorers read when rating an opportunity on this criterion.",
    doHere: [
      "Review the <strong>five criticality levels</strong> and the <em>business definition</em> behind each.",
      "Click <strong>Edit</strong> to refine a level's <em>name</em>, <em>definition</em>, <em>typical characteristics</em> and <em>color</em>.",
      "Use the per-level <strong>color</strong> so criticality reads at a glance across the platform.",
      "Toggle a level <strong>active</strong> to control whether it appears as a scoring option."
    ],
    fits: 'Frameworks / Scoring config &rarr; <strong>Scoring criteria</strong> &rarr; Opportunity scoring &rarr; Recommendation',
    firstTime: "Open <strong>Edit</strong> on the top level and check its <em>Typical Characteristics</em> match how your organization defines mission-critical work.",
    sections: [
      {
        label: 'The 1–5 criticality scale',
        type: 'defs',
        items: [
          { term: 'Score 5', color: '#4CAF50', def: "<strong>Mission-critical</strong> &mdash; central to core operations or revenue; failure has severe business impact." },
          { term: 'Score 4', color: '#8BC34A', def: "<strong>High importance</strong> &mdash; significant to key processes or customers." },
          { term: 'Score 3', color: '#FFC107', def: "<strong>Moderate</strong> &mdash; useful and valued but not business-critical." },
          { term: 'Score 2', color: '#FF9800', def: "<strong>Low importance</strong> &mdash; limited reach or marginal business effect." },
          { term: 'Score 1', color: '#F44336', def: "<strong>Minimal</strong> &mdash; nice-to-have with little operational consequence." }
        ],
        note: "Each level carries a <strong>business definition</strong> and <strong>typical characteristics</strong> to anchor consistent rating. Colors are configurable per level."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Opportunity scoring →', href: '/portfolio_definition/opportunities/opportunity-registry' }
    ]
  };

/** settings/delivery-risk — cloned from `views/setup-delivery-risk.ejs`. */
export const DELIVERY_RISK: HelpContent = {
    title: 'Delivery Risk Scale',
    icon: '⚠️',
    purpose: "This screen defines the <strong>1&ndash;5 scoring scale</strong> for <strong>Delivery Risk</strong> &mdash; how hard and uncertain an opportunity is to deliver. Each level pairs a definition with <strong>risk indicators</strong> so scorers rate execution difficulty consistently. <em>Note: here a higher score means higher risk.</em>",
    doHere: [
      "Review the <strong>five risk levels</strong> and the signals that distinguish them.",
      "Click <strong>Edit</strong> to refine a level's <em>name</em>, <em>description</em> and <em>risk indicators</em>.",
      "List concrete <strong>risk indicators</strong> (dependencies, data gaps, change scope) raters can spot.",
      "Toggle a level <strong>active</strong> to control its availability as a scoring option."
    ],
    fits: 'Frameworks / Scoring config &rarr; <strong>Scoring criteria</strong> &rarr; Opportunity scoring &rarr; Recommendation',
    firstTime: "Open <strong>Edit</strong> on the top level and add the <em>risk indicators</em> that flag a high-risk delivery in your organization.",
    sections: [
      {
        label: 'The 1–5 risk scale (higher = riskier)',
        type: 'defs',
        items: [
          { term: 'Score 5', color: '#D32F2F', def: "<strong>Very high risk</strong> &mdash; major unknowns, heavy dependencies, low confidence in delivery." },
          { term: 'Score 4', color: '#FF5722', def: "<strong>High risk</strong> &mdash; significant complexity or unresolved dependencies." },
          { term: 'Score 3', color: '#FF9800', def: "<strong>Moderate risk</strong> &mdash; manageable challenges with some uncertainty." },
          { term: 'Score 2', color: '#FFC107', def: "<strong>Low risk</strong> &mdash; well-understood scope, few dependencies." },
          { term: 'Score 1', color: '#4CAF50', def: "<strong>Minimal risk</strong> &mdash; proven approach, clear path to delivery." }
        ],
        note: "Unlike the value criteria, a <strong>higher score signals more risk</strong>. Each level can carry <strong>risk indicators</strong> to make rating objective."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Opportunity scoring →', href: '/portfolio_definition/opportunities/opportunity-registry' }
    ]
  };

/** settings/framework-builder — cloned from `views/framework-builder.ejs`. */
export const FRAMEWORK_BUILDER: HelpContent = {
  title: 'Framework Builder',
  icon: '🌳',
  purpose: "Build an entire scoring framework on <strong>one screen</strong>. Every row is a <strong>dimension &rarr; component &rarr; question</strong> with its <strong>weight</strong>, and you can expand any question to edit its <strong>answer choices</strong> and scores inline.",
  doHere: [
    "<strong>Add dimensions</strong>, then components and questions to flesh out the framework's structure.",
    "Set each item's <strong>weight</strong> (1&ndash;5 dots) &mdash; higher weight means it counts more toward the score.",
    "Expand a question's <strong>Q&amp;A</strong> to define its answer choices and the 1&ndash;5 score each maps to.",
    "Filter by <strong>Business</strong> or <strong>Technology</strong> dimensions to focus while editing."
  ],
  fits: 'Frameworks &rarr; Framework Builder (Dimensions / Components / Questions / Weights) &rarr; Mark Complete &rarr; Scoring',
  firstTime: "Click <strong>Add dimension</strong>, name it, then add a component and a question underneath. Set weights with the dots and expand the question to edit answer choices.",
  sections: [
    {
      label: 'Framework hierarchy',
      type: 'html',
      html: "Each framework is a tree &mdash; build it top-down:<div style='margin-top:10px; font-size:12.5px; color:#546e7a; line-height:1.95;'><strong style='color:#1a237e;'>Dimension</strong> &rarr; <strong style='color:#1565c0;'>Component</strong> &rarr; <strong style='color:#6a1b9a;'>Question</strong> &rarr; Answer choices (each scored 1&ndash;5)</div>"
    },
    {
      label: 'Weights',
      type: 'defs',
      items: [
        { term: '1 — Low', color: '#E53935', def: "Minor influence on the overall score." },
        { term: '3 — Medium', color: '#FDD835', def: "Standard, balanced weighting." },
        { term: '5 — High', color: '#2E7D32', def: "Drives the score &mdash; this item matters most." }
      ],
      note: "Set weights by clicking the colored dots on each row. Answer scores (1&ndash;5) feed the weighted average that produces an opportunity's readiness score."
    }
  ],
  links: [
    { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
    { label: 'Back to all frameworks →', href: '/portfolio_definition/settings/assessment-framework/frameworks' }
  ]
};

/** settings/framework-designer — cloned from `views/frameworks/designer.ejs`. */
export const FRAMEWORK_DESIGNER: HelpContent = {
  title: 'Framework Designer',
  icon: '🧮',
  purpose: "A <strong>scoring framework</strong> is the lens every opportunity is judged through. Here you visually compose that lens — its <strong>dimensions</strong>, the <strong>components</strong> inside them, the <strong>questions</strong> that gather evidence, and the <strong>weights</strong> that decide how much each one matters.",
  doHere: [
    "<strong>Add and arrange dimensions</strong> (the top-level scoring themes) on the board.",
    "<strong>Add components</strong> under each dimension and set their <strong>weightings</strong> so the score reflects what matters most.",
    "Drag to <strong>reorder</strong>, then <strong>Save Layout</strong> to persist the structure.",
    "Switch between frameworks with the selector, or <strong>Clone</strong> a locked one to make an editable copy."
  ],
  fits: 'Define Framework &rarr; <strong>Designer</strong> &rarr; Assign to Project &rarr; Score Opportunities',
  firstTime: "Pick a framework in the selector, then use <strong>Add Dimension</strong> and <strong>Add Component</strong> to build it out. Click <strong>Save Layout</strong> when the structure looks right.",
  sections: [
    {
      label: 'How a framework is built',
      type: 'html',
      html: "Each level nests inside the one above it, and weights roll the detailed scores up into a single number:<div style='margin-top:10px; font-size:12.5px; color:#546e7a; line-height:1.95;'>Dimension &rarr; Component &rarr; Question <span style='color:#90a4ae;'>(evidence)</span> &rarr; <strong style='color:#1565c0;'>Weighted score</strong></div>"
    },
    {
      label: 'Locked frameworks',
      type: 'defs',
      items: [
        { term: 'Editable', color: '#1976d2', def: "Not yet assigned to a live project — you can freely add, reorder and reweight dimensions and components." },
        { term: 'Locked', color: '#f59e0b', def: "Already in use by one or more projects, so its structure is frozen to keep past scores comparable. Clone it to make changes." }
      ],
      note: "Use <strong>Clone Framework</strong> to spin off a customizable copy without disturbing the version projects already rely on."
    }
  ],
  links: [
    { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
    { label: 'Next: Frameworks →', href: '/portfolio_definition/settings/assessment-framework/frameworks' }
  ]
};

/** settings/frameworks — cloned from `views/frameworks/list.ejs`. */
export const FRAMEWORKS_LIST: HelpContent = {
    title: 'Framework Management',
    icon: '🎯',
    purpose: "A <strong>framework</strong> is the scoring rulebook every opportunity is measured against. This screen lists all frameworks &mdash; each made of <strong>dimensions &rarr; components &rarr; questions &rarr; answer choices</strong> &mdash; and tracks how complete each one is before it can be used to score.",
    doHere: [
      "<strong>Create</strong> a new framework, or <strong>Clone</strong> an existing one to start from a proven design.",
      "Open the <strong>Designer</strong> to build out a framework's dimensions, components and questions.",
      "Watch the <strong>Completeness</strong> bar &mdash; it tracks the share of components that have questions; you need <strong>100%</strong> before completing.",
      "<strong>Mark Complete</strong> when 100% to lock the design as ready; Administrators can <strong>Revert</strong> a completed framework back to In Design."
    ],
    fits: 'Frameworks &rarr; Designer (Dimensions / Components / Questions) &rarr; Projects &rarr; Scoring',
    firstTime: "Click <strong>Create Framework</strong>, name it, then click <strong>Designer</strong> on its row to start adding dimensions and components.",
    sections: [
      {
        label: 'Framework status',
        type: 'defs',
        items: [
          { term: 'IN DESIGN', color: '#9e9e9e', def: "Being built &mdash; dimensions, components and questions can be freely added and edited. Reach 100% completeness to finish it." },
          { term: 'COMPLETE', color: '#2e7d32', def: "Design is finished and validated &mdash; ready to assign to projects and score against. Editing is closed (Administrators can Revert)." },
          { term: 'LOCKED', color: '#e65100', def: "In active use &mdash; opportunities have already been scored against it, so its structure is frozen to protect existing results." }
        ],
        note: "The <strong>Dim</strong> / <strong>Comp</strong> / <strong>Q's</strong> columns count the framework's dimensions, components and questions. <strong>Completeness</strong> turns green at 100%."
      },
      {
        label: 'Row actions',
        type: 'defs',
        items: [
          { term: 'Designer', color: '#1976d2', def: "Open the framework in the Framework Builder to compose its dimensions, components and questions." },
          { term: 'Edit', color: '#ff9800', def: "Rename or re-describe the framework (available while In Design)." },
          { term: 'Clone', color: '#4caf50', def: "Duplicate the whole framework as a new editable copy." },
          { term: 'Complete', color: '#2196f3', def: "Mark a 100%-complete framework as Design Complete." }
        ]
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Framework Designer →', href: '/portfolio_definition/settings/assessment-framework/framework-designer' }
    ]
  };

/** settings/process-hierarchy — cloned from `views/opportunities-process-hierarchy.ejs`. */
export const PROCESS_HIERARCHY: HelpContent = {
    title: 'Process Hierarchy (L1–L3)',
    icon: '🗂️',
    purpose: "The reference taxonomy that opportunities are classified against. It's a three-level tree &mdash; <strong>L1 Process Groups</strong> &rarr; <strong>L2 Process Areas</strong> &rarr; <strong>L3 Activities</strong> &mdash; so every initiative can be grouped, filtered and analyzed by where it sits in the business.",
    doHere: [
      "Switch between the <strong>L1</strong>, <strong>L2</strong> and <strong>L3</strong> tabs to browse each level of the hierarchy.",
      "<strong>Add</strong>, edit or deactivate entries, and set their display order.",
      "Link <strong>L2</strong> areas to their parent <strong>L1</strong> group, and <strong>L3</strong> activities to their parent <strong>L2</strong> area.",
      "Keep the taxonomy current so opportunity classification and portfolio analysis stay accurate."
    ],
    fits: 'Reference data for the <strong>Score</strong> stage — opportunities are classified against this hierarchy',
    firstTime: "Open the <strong>L1 Process Groups</strong> tab to see the top level, then drill into <strong>L2</strong> and <strong>L3</strong> to understand the full tree.",
    sections: [
      {
        label: 'The three levels',
        type: 'defs',
        items: [
          { term: 'L1', color: '#1976d2', def: "<strong>Process Groups</strong> — the broadest categories of business activity (the top of the tree)." },
          { term: 'L2', color: '#00897b', def: "<strong>Process Areas</strong> — functional areas nested under each L1 group." },
          { term: 'L3', color: '#5e35b1', def: "<strong>Activities</strong> — the specific activities under each L2 area, the finest grain of classification." }
        ],
        note: "Every opportunity is tagged to an <strong>L1 &rarr; L2 &rarr; L3</strong> path, which powers the process-group filters and distribution charts elsewhere."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Opportunity Registry →', href: '/portfolio_definition/opportunities/opportunity-registry' }
    ]
  };

/** settings/project-visibility — cloned from `views/setup-project-execution-visibility.ejs`. */
export const PROJECT_VISIBILITY: HelpContent = {
    title: 'Project Visibility',
    icon: '🔐',
    purpose: "Control <strong>who can access each delivery project</strong> in the execution module. Anyone granted here can open that project's execution screens; everyone else won't even see it in their project pickers. <strong>Administrators</strong> and <strong>Strategic Analysts</strong> always have full access.",
    doHere: [
      "Pick a <strong>delivery project</strong> to manage.",
      "Toggle access on for the <strong>team members</strong> (pulled from the project's streams) who should see it.",
      "Optionally <strong>add a login user</strong> who isn't on the team roster.",
      "Newly granted people start as <strong>View-only</strong> until a project role is chosen for them."
    ],
    fits: 'Setup &rarr; <strong>Project Execution</strong> &rarr; Program Management',
    firstTime: "Select your project, add the people who should have access, and give each one the role they hold on it.",
    /*
     * THE ROLE MODEL, extrapolated here on the owner's instruction (2026-08-21) from
     * `User_Role_Access_Model_1.docx`.
     *
     * It belongs on this screen because this is where access begins: granting someone a
     * project here is what makes their role mean anything. Without it, the two questions
     * "can this person see the project" and "what may they do once inside" look like the
     * same control, and they are not.
     */
    sections: [
      {
        label: 'The five roles, and what each is for',
        type: 'defs',
        items: [
          {
            term: '1',
            color: '#5c6b7a',
            def: "<strong>Contributor / Team Member</strong> &mdash; developer, analyst, tester, scrum team member. Sees and updates <em>only the work assigned to them</em>, including its percent complete. Cannot create, delete or reassign work, cannot see another member's items, and cannot see budget."
          },
          {
            term: '2',
            color: '#1565c0',
            def: "<strong>Project Manager</strong> &mdash; full create/edit/delete on the projects they manage, reassigns work between people on those projects, manages their team, and proposes budget. Cannot create new projects, cannot approve their own budget or stage gates, and cannot see projects they do not manage."
          },
          {
            term: '3',
            color: '#00796b',
            def: "<strong>Program / Portfolio Manager</strong> &mdash; works <em>across</em> projects: rolls up status, moves resources between projects, creates new projects, approves program-level budget. Deliberately does <em>not</em> edit an individual item's day-to-day detail &mdash; that stays with the Project Manager."
          },
          {
            term: '4',
            color: '#8a6d1b',
            def: "<strong>Executive Sponsor</strong> &mdash; sits <em>above</em> Program Manager, not beside it. Everything a Program Manager can do, at every level, plus final governance authority: stage gates, funding, formal decisions on the record. Full access to every project, not only the ones they sponsor. Views the audit log."
          },
          {
            term: '5',
            color: '#b3261e',
            def: "<strong>System Administrator</strong> &mdash; accounts, passwords, role assignment, configuration and integrations. Kept separate from Executive Sponsor because these are IT and security functions, not business decisions."
          }
        ],
        note: "Roles map to <strong>what someone needs to do</strong>, not to their job title &mdash; many titles collapse into one role. A Scrum Master is usually the Project Manager role scoped to one team, not a sixth role."
      },
      {
        label: 'Two things about the model that look like mistakes and are not',
        type: 'html',
        html: "<strong>Permissions scale with accountability, not seniority.</strong> A steering committee member outranks a Project Manager on the org chart but has fewer edit rights in the tool. In the same way, a Program Manager cannot update an individual task's status or delete a deliverable &mdash; a &ldquo;higher&rdquo; role with fewer rights in one column is the design, deliberately, because that detail belongs to the person accountable for it.<br><br><strong>Administrator is a scope, not a rank.</strong> Executive Sponsor sits at the top of the <em>business</em> hierarchy and still cannot create accounts or assign roles. That is not an oversight: account and security actions are IT functions and stay with Administrator, who in turn is not meant to be used for routine project work."
      },
      {
        label: 'Access here, permissions next door',
        type: 'html',
        html: "This screen answers two questions: <strong>can this person open this project</strong>, and <strong>what role do they hold on it</strong>. The role is what the permission model above attaches to.<br><br>Newly granted people start <strong>View-only</strong> by design, so granting access is never the same act as granting edit rights. Switching someone off removes their row entirely rather than recording &ldquo;no access&rdquo;: a missing row hides the project, where an explicit &ldquo;none&rdquo; would leave it visible but empty."
      }
    ],
  };

/** settings/recommendation — cloned from `views/setup-scoring.ejs`. */
export const RECOMMENDATION: HelpContent = {
    title: 'Recommendation Scoring',
    icon: '📊',
    purpose: "Defines the <strong>readiness scoring scale</strong> &mdash; the score bands that turn a numeric assessment result (1.00&ndash;5.00) into a clear <strong>recommendation</strong> and color. Every scored opportunity is placed into one of these tiers.",
    doHere: [
      "Review the <strong>score-range thresholds</strong> and the recommendation text each one produces.",
      "<strong>Edit</strong> a threshold to adjust its score range, label, color or recommendation wording.",
      "Keep ranges <strong>contiguous and non-overlapping</strong> from 1.00 to 5.00 so every score maps to exactly one tier.",
      "Confirm the <strong>color</strong> of each band &mdash; it is reused across dashboards and reports."
    ],
    fits: 'Frameworks &rarr; Scoring &rarr; Opportunities scored &rarr; Recommendation tier &rarr; Dashboards',
    firstTime: "Click <strong>Edit</strong> on a threshold to see how a range maps to a recommendation, then adjust the wording or color.",
    sections: [
      {
        label: 'Recommendation tiers',
        type: 'defs',
        items: [
          { term: 'Discontinue / Defer', color: '#c62828', def: "Score 1.00&ndash;1.99 &mdash; not ready; stop or postpone." },
          { term: 'Major Concerns', color: '#ef6c00', def: "Score 2.00&ndash;2.49 &mdash; serious gaps to resolve before proceeding." },
          { term: 'Conditional Proceed', color: '#f9a825', def: "Score 2.50&ndash;3.49 &mdash; proceed only if specific conditions are met." },
          { term: 'Recommended', color: '#2e7d32', def: "Score 3.50&ndash;4.24 &mdash; solid case; recommended to advance." },
          { term: 'Fast Track', color: '#1565c0', def: "Score 4.25&ndash;5.00 &mdash; strong case; prioritize for fast delivery." }
        ],
        note: "These five tiers are the standard scale. Each <strong>threshold card</strong> shows its range, color and the recommendation text shown to scorers."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Opportunities →', href: '/portfolio_definition/opportunities/opportunity-registry' }
    ]
  };

/** settings/screen-permissions — cloned from `views/setup-project-execution-permissions.ejs`. */
export const SCREEN_PERMISSIONS: HelpContent = {
    title: 'Screen Permissions',
    icon: '🧭',
    purpose: "For people who can access a delivery project, decide <strong>which execution screens</strong> they can <strong>view</strong> or <strong>edit</strong>. Use a quick <strong>preset</strong> for common roles, or switch to <strong>Custom</strong> to allow only specific screens. <strong>Administrators</strong> and <strong>Strategic Analysts</strong> always have full access.",
    doHere: [
      "Pick a <strong>delivery project</strong> &mdash; only people granted in <em>Project Visibility</em> appear here.",
      "Choose a <strong>preset</strong>: <em>Full access</em> (all screens, edit), <em>View-only</em> (all screens, read), <em>Custom</em>, or <em>No access</em>.",
      "For <strong>Custom</strong>, tick exactly which screens the person may <em>view</em> and/or <em>edit</em>.",
      "Example: a Project Manager or Steering Committee member gets <em>Full access</em>; a team member gets <em>Custom</em> with just the Control Center."
    ],
    fits: 'Setup &rarr; Project Visibility &rarr; <strong>Screen Permissions</strong> &rarr; Program Management',
    firstTime: "Give your PM and key stakeholders <strong>Full access</strong>, then set individual team members to <strong>Custom</strong> with only the screens they need.",
    links: [
      { label: '← Back: Project Visibility', href: '/portfolio_definition/settings/project-execution/project-visibility' }
    ]
  };

/** settings/strategic-alignment — cloned from `views/setup-ai-alignment.ejs`. */
export const STRATEGIC_ALIGNMENT: HelpContent = {
    title: 'Strategic Alignment Scale',
    icon: '🧭',
    purpose: "This screen defines the <strong>1&ndash;5 scoring scale</strong> for <strong>Strategic Alignment</strong> &mdash; how closely an opportunity fits the organization's defined <strong>strategy and technology architecture</strong>. These level definitions are the rubric scorers read when they rate an opportunity on this criterion.",
    doHere: [
      "Review the <strong>five levels</strong> (Score 5 down to Score 1) and what each one means.",
      "Click <strong>Edit</strong> on any level to refine its <em>name</em> and <em>description</em> so raters apply it consistently.",
      "Toggle a level <strong>active</strong> or inactive to control whether it appears as a scoring option.",
      "Keep the language concrete &mdash; this is the shared definition every scorer relies on."
    ],
    fits: 'Frameworks / Scoring config &rarr; <strong>Scoring criteria</strong> &rarr; Opportunity scoring &rarr; Recommendation',
    firstTime: "Read the <strong>Score 5</strong> level, then open <strong>Edit</strong> to see how a level is described. Tighten wording wherever a rater could be unsure.",
    sections: [
      {
        label: 'The 1–5 alignment scale',
        type: 'defs',
        items: [
          { term: 'Score 5', color: '#4CAF50', def: "<strong>Fully aligned</strong> &mdash; directly advances core strategy and fits the target technology architecture." },
          { term: 'Score 4', color: '#8BC34A', def: "<strong>Strongly aligned</strong> &mdash; supports strategic goals with only minor architectural gaps." },
          { term: 'Score 3', color: '#FFC107', def: "<strong>Moderately aligned</strong> &mdash; partial fit; some strategy or architecture tension to manage." },
          { term: 'Score 2', color: '#FF9800', def: "<strong>Weakly aligned</strong> &mdash; tangential to strategy or against the architectural direction." },
          { term: 'Score 1', color: '#F44336', def: "<strong>Not aligned</strong> &mdash; no clear strategic fit; conflicts with the technology architecture." }
        ],
        note: "Higher score = stronger strategic fit. This is one of several criteria that combine into an opportunity's overall recommendation."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
      { label: 'Next: Opportunity scoring →', href: '/portfolio_definition/opportunities/opportunity-registry' }
    ]
  };

/*
 * ─────────────────────────────────────────────────────────────────────────────
 * WRITTEN, NOT PORTED — the two screens below have no help object in the source.
 *
 * Program Quick Start is standalone and first here on the owner's instruction, where the
 * source buries it under Strategic Intelligence; Delivery Pathway gates what the Portfolio
 * Optimizer will even look at, which is the single most confusing thing in this module and
 * had no explanation anywhere. Both follow the source's own shape and register so they read
 * as part of the same set.
 */

/** program-quick-start — no source help object; written for this screen. */
export const PROGRAM_QUICK_START: HelpContent = {
  title: 'Program Quick Start',
  icon: '🚀',
  purpose:
    "Turn a plan you already have into <strong>projects and opportunities</strong>. Upload the document — a slide deck, a spreadsheet, even a screenshot of a plan — and it is read for numbered projects and the work items under them, then proposed back to you before anything is written.",
  doHere: [
    "<strong>Upload one or more files</strong> (.pdf, .docx, .xlsx, .pptx, images). Several files are read <em>together</em>: one may list the projects and another their opportunities.",
    "Press <strong>Analyse &amp; Preview</strong> and review what was found. <strong>Nothing is saved at this point.</strong>",
    "Tick the projects and opportunities you want, resolve any duplicates, then <strong>Generate</strong>.",
  ],
  fits: '<strong>Quick Start</strong> &rarr; Group into projects &rarr; Score &rarr; Finance &rarr; Optimize',
  firstTime:
    "Upload the plan you already keep — it does not need reformatting. Review the preview, untick anything you do not want, and generate.",
  sections: [
    {
      label: 'What it looks for',
      type: 'defs',
      items: [
        { term: 'Project', color: '#1976d2', def: 'The container that carries a <strong>number or code</strong> and groups work together. Your deck may call it an initiative, programme, workstream or release &mdash; the wording is yours, the structure is what matters.' },
        { term: 'Opportunity', color: '#00897b', def: 'The individual numbered items under a project, usually with their own dates. Called milestones, deliverables, work packages or features in different plans.' },
        { term: 'Duplicate', color: '#f57c00', def: 'A project or opportunity whose id already exists here. You choose per row: <strong>merge</strong> into the existing one, create it as <strong>new</strong>, or <strong>skip</strong> it.' },
      ],
      note: 'Analysis needs an API key on the server. If one is missing the screen says so directly rather than blaming your file.',
    },
  ],
  links: [{ label: 'Next: Project Registry →', href: '/portfolio_definition/projects/project-registry' }],
};

/**
 * delivery-pathway — the SOURCE'S OWN DRAWER, `views/delivery-pathway.ejs:250-274`.
 *
 * That screen does not use the shared help partial; it ships a bespoke drawer holding both
 * "How the Delivery Pathway works" AND the Guardrails. The port rendered the guardrails
 * INLINE on the page instead, which ate the vertical space the two panels needed. The
 * owner's ruling (2026-08-09): take it off the screen and put it behind an info control.
 *
 * So the copy below is the source's drawer, carried across verbatim — its intro, its A/B
 * pair, its five guardrails and its closing note.
 */
export const DELIVERY_PATHWAY: HelpContent = {
  title: 'How the Delivery Pathway works',
  icon: '🛤️',
  purpose:
    "Every opportunity reaches delivery one of two ways. Pick a project on the left, select the opportunities, then apply a pathway &mdash; you can mix pathways within a project.",
  sections: [
    {
      type: 'defs',
      items: [
        { term: 'A · Express', color: '#0d7a63', def: 'The analysis is already done. Selected opportunities go <strong>straight to a delivery project</strong> and run through execution as-is &mdash; scoring, modeling and optimization are skipped.' },
        { term: 'B · Guided', color: '#1e5aa8', def: 'Walk the value chain, but only the stages you choose. <strong>Effort &amp; Value is required</strong>; everything above the line (Score, Platform Fit, Financial, Optimizer) is optional.' },
      ],
    },
    {
      label: 'Guardrails',
      type: 'defs',
      items: [
        /*
          THE SOURCE'S FIRST GUARDRAIL NO LONGER APPLIES HERE, so its copy is not carried
          across — printing a rule the software does not enforce is worse than rewording it.
          The owner lifted it on 2026-08-09 because planning and execution are separate
          modules on this platform and execution does not read the pathway column at all.
        */
        { term: '↔ Both lanes', color: '#b45309', def: '<b>An opportunity can sit on both.</b> Assign it to Option B for planning while its delivery project runs on in execution &mdash; moving it takes nothing away, because execution tracks its own delivery-project records rather than this setting. A <b>▶</b> in the list marks work already under way.' },
        { term: '🚫 Hidden', color: '#b45309', def: '<b>Option A skips the analysis screens.</b> A-lane opportunities do <b>not</b> appear in the Option B screens (Portfolio Optimizer, Effort &amp; Value, Benefit Realization, Disposition, Roadmap Planning). They reappear only in Program Management, where both lanes converge.' },
        { term: '👁 Optimizer', color: '#b45309', def: "<b>Portfolio Optimizer is Option B only.</b> Only B-lane opportunities can be pulled into the optimizer &mdash; it's optional, but never shows A-lane work." },
        { term: '🌍 Financial', color: '#b45309', def: '<b>Financial Modeling stays open to all.</b> Any project can run MVP / NPV at any time, whichever pathway it&rsquo;s on.' },
        { term: '💰 Budget', color: '#b45309', def: '<b>No budget needed up front for Option A.</b> Budget is captured later, at the Budget Plan step inside execution.' },
      ],
      note: 'Each stage in the two flows is a shortcut &mdash; <strong>double-click a bucket</strong> to open that module in a new tab, so this screen stays put while you look.',
    },
  ],
  links: [{ label: 'Next: Strategy and Constraints →', href: '/portfolio_definition/portfolio-optimizer/strategy-and-constraints' }],
};

/**
 * Program Quick Start — Analysis.
 *
 * Copied from the source's `qsaHelp` in `views/roadmap-execution/quick-start-analysis.ejs`
 * (lines 111-142), wording and entities intact. Only the two links are re-pointed at this
 * app's routes — the source's `/roadmap-execution/...` paths do not exist here.
 */
export const QUICK_START_ANALYSIS: HelpContent = {
  title: 'Program Quick Start — Analysis',
  icon: 'manage_search',
  purpose:
    'Read what an <strong>analysis run</strong> proposed &mdash; the projects, initiatives and work items the AI derived from your uploaded material &mdash; and generate <strong>exactly the slice you choose</strong>. The proposal is never written on its own: nothing reaches the database until you press <strong>Generate selected</strong>.',
  doHere: [
    'Pick an <strong>analysis run</strong> from the list (most recent first). The <strong>i</strong> button beside it opens everything the run recorded &mdash; source files, instructions and counts.',
    "Choose a <strong>Project</strong> &mdash; one, several with Ctrl/&#8984;-click, or <strong>All projects</strong>. The <strong>Initiative</strong> picker then narrows to that project's initiatives only.",
    'Read the proposal below: each project is separated, its initiatives are indented beneath it, and every work item is marked <strong>D</strong> (Deliverable) or <strong>A</strong> (Activity).',
    'Tick what you want &mdash; or <strong>Select all</strong> / <strong>Clear all</strong>. The counter shows the exact number of projects, initiatives and work items you are about to create.',
    'Click <strong>Generate selected</strong>. Everything created is tagged to this run, so <strong>Delete generated data</strong> removes precisely what the run made and nothing else.',
  ],
  fits: 'Program Quick Start &rarr; <strong>Analysis</strong> &rarr; Roadmap Definition &rarr; Work Item Definition',
  firstTime:
    'Pick the newest run, choose <strong>All projects</strong>, read what it proposes, then Select all and Generate selected.',
  sections: [
    {
      label: 'Reading the proposal',
      type: 'defs',
      items: [
        {
          term: 'D',
          color: '#1565c0',
          def: '<strong>Deliverable</strong> &mdash; a work item that produces an artifact (a document, a build, a signed-off design).',
        },
        {
          term: 'A',
          color: '#c62828',
          def: '<strong>Activity</strong> &mdash; work that has to happen but leaves no artifact behind (a workshop, a review, a cutover rehearsal).',
        },
        {
          term: 'Phase',
          color: '#42a5f5',
          def: 'Each work item sits in one of the five delivery phases, colour-coded and shown with the dates the run derived for it. <em>TBD</em> means the source material carried no date.',
        },
      ],
    },
    {
      label: 'Nothing is generated until you say so',
      type: 'html',
      html: 'An analysis run only ever produces a <strong>proposal</strong>. You can open it, filter it, re-read it and close it as often as you like without changing any data. Only <strong>Generate selected</strong> writes, and only the rows you ticked.<br><br>Because every generated record is tagged with its run, <strong>Delete generated data</strong> is a clean reversal &mdash; it removes what this run created and leaves anything you built by hand untouched.',
    },
  ],
  links: [
    { label: '\u2190 Back to Program Quick Start', href: '/portfolio_definition/program-quick-start' },
    { label: 'Next: Roadmap Definition \u2192', href: '/project_management/roadmap-definition' },
  ],
};
